/**
 * Responsible for showing/hiding content based on a tab structure
 * @module modules/tabs
 */
import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';

const JS_TABS_DEFAULT_OPTIONS = {
  tab: '.tabs__link',
  tabContent: '.tabs__content',
  tabContentActive: 'tabs__content--active',
  tabActive: 'tabs__link--active',
  tabNav: '.tabs__nav',
  tabSlider: '.tabs__presentation-slider',
  tabFixed: '.tabs__nav--fixed',
  onShow: () => {},
};

const makeToggleTab = ($tab, initialActiveClass, activeClass) => ($target) => {
  $tab
    .removeClass(activeClass)
    .attr('aria-selected', 'false')
    .find('.u-screen-reader')
    .remove();

  $target
    .addClass(activeClass)
    .attr('aria-selected', 'true')
    .append('<span class="u-screen-reader" aria-live="assertive"> has been selected</span>');

  if (initialActiveClass) {
    $tab.removeClass(initialActiveClass);
    $target.addClass(initialActiveClass);
  }
};

const makeToggleContent = ($content, tabContentActive, onShow) => ($target) => {
  const contentSelector = $target.data('tab-content');
  $content.removeClass(tabContentActive);
  $(contentSelector).addClass(tabContentActive);
  onShow();
};

const getWidthOfTabBar = ($el, tabNav) => {
  return $el.find(tabNav).outerWidth();
}

const makeCalculation = ($el, tabSlider, tabNav) => ($target) => {

  // Get the length of the entire tab navigation bar
  const tabNavFullLength = getWidthOfTabBar($el, tabNav);

  // Get the length of the active tab
  const tabActiveLength = $target.outerWidth();

  // Because each tab can have different with
  // we need to divide the active tab length by the
  // overall length of the navigation bar.
  const tabLengthScale = tabActiveLength / tabNavFullLength

  // Calculate the position of the scroll (if tab is scrollable)
  const scrollLeftPosition = $el.find(tabNav).scrollLeft();

  // We get the position of the active tab from the left of
  // the tab bar. We need to also add from the scroll position.
  const tabPositionFromLeft = $target.position().left + scrollLeftPosition;

  // We need to calculate the % offset of where the slider will
  // be placed. ie. first tab will have a 0% offset.
  const positionOffset = ((tabPositionFromLeft / tabNavFullLength) * 100);

  // Finally, we have to add inline styling, transform because its dynamic based on
  // the number of tabs we're going to be having
  $el.find(tabSlider).css('transform', 'translateX('+positionOffset+'%) scale('+ tabLengthScale +')')
}

const tabs = (el, options = {}) => {
  const $el = $(el);
  const {
    tab,
    tabContent,
    tabActive,
    tabContentActive,
    tabNav,
    tabSlider,
    tabFixed,
    onShow,
  } = Object.assign({}, JS_TABS_DEFAULT_OPTIONS, $el.data(), options);

  const $tab = $el.find(tab).filter(function () {
    return $(this).closest('.js-tabs')[0] === $el[0];
  });


  const $content = $el.find(tabContent).filter(function () {
    return $(this).closest('.js-tabs')[0] === $el[0];
  });

  const activeTabClass = $el.data('active-tab') ? $el.data('active-tab').replace('.', '') : false;

  const toggleTab = makeToggleTab($tab, activeTabClass, tabActive);
  const toggleContent = makeToggleContent($content, tabContentActive, onShow);

  const $initialActiveTab = $el.find(`.${tabActive}`);

  if ($initialActiveTab.length) {
    $initialActiveTab.attr('aria-selected', 'true');
  }

  let calculateTarget;
  if ($el.find(tabFixed).length) {
    calculateTarget = makeCalculation($el, tabSlider, tabNav);
    // initiate the first active tab and calculate the width
    calculateTarget($(tabFixed).find(`.${tabActive}`))
  }

  $tab.on('click', (e) => {
    e.preventDefault();

    const $target = $(e.currentTarget);

    // Ensures that fixed tabs don't have scroll feature.
    if ($el.find(tabFixed).length === 0) {
      $target[0].scrollIntoView({ behavior: "smooth", block: 'nearest', inline: 'nearest' });
    }

    toggleTab($target);
    toggleContent($target);

    if ($el.find(tabFixed).length) {
      calculateTarget($target);
    }
  });
};

registerJQueryPlugin('tabs', tabs);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/tabs.js